// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import type {IssueViewerQueryIdentifier} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useMutation, type UseMutationResult, useQueryClient} from '@github-ui/react-query'
import {convertIssueToDiscussionMutation as convertIssueToDiscussionMutationQueryId} from '@github-ui/server-persisted-queries'
import type {
  ConvertIssueToDiscussionInput as ServerConvertIssueToDiscussionInput,
  ConvertIssueToDiscussionMutationMutation,
} from '@github-ui/server-persisted-queries/types/convertIssueToDiscussionMutation'

import {issueLifecyclePath, submitIssueLifecycleMutation} from './issue-lifecycle-mutation'
import {removeIssueViewerLifecycleCaches} from './remove-issue-viewer-lifecycle-caches'

export type ConvertIssueToDiscussionRouteInput = Omit<
  ServerConvertIssueToDiscussionInput,
  'issueId' | 'clientMutationId'
>
export type ConvertIssueToDiscussionMutationResponse = ConvertIssueToDiscussionMutationMutation

export function useConvertIssueToDiscussionMutation({
  owner,
  repository,
  number,
  primaryIdentifier,
  onCompleted,
  onError,
}: {
  owner: string
  repository: string
  number: number
  primaryIdentifier?: IssueViewerQueryIdentifier
  onCompleted?: (response: ConvertIssueToDiscussionMutationMutation) => void | Promise<void>
  onError?: (error: Error) => void
}): UseMutationResult<ConvertIssueToDiscussionMutationMutation, Error, {input: ConvertIssueToDiscussionRouteInput}> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({input}) => submitConvertIssueToDiscussionMutation({owner, repository, number, input}),
    onSuccess: async response => {
      try {
        await removeIssueViewerLifecycleCaches({owner, repository, number, primaryIdentifier, queryClient})
      } catch (error) {
        reportError(error instanceof Error ? error : new Error('Issue conversion cache cleanup failed', {cause: error}))
      }
      await onCompleted?.(response)
    },
    onError,
  })
}

export function submitConvertIssueToDiscussionMutation({
  owner,
  repository,
  number,
  input,
}: {
  owner: string
  repository: string
  number: number
  input: ConvertIssueToDiscussionRouteInput
}) {
  return submitIssueLifecycleMutation<ConvertIssueToDiscussionMutationMutation>({
    path: `${issueLifecyclePath(owner, repository, number)}/discussion.json`,
    method: 'POST',
    queryId: convertIssueToDiscussionMutationQueryId,
    input,
    errorMessage: 'Issue conversion mutation failed',
    isSuccess: data => data.convertIssueToDiscussion?.discussion != null,
    payloadErrors: data => data.convertIssueToDiscussion?.errors,
  })
}
